/**
 * Shared error classifier for the marketing app.
 *
 * Every page that calls into the backend needs the same decision:
 * "is this something the user can fix (bad password, duplicate
 * email, too many requests) or something on our side (500, SMTP
 * broken, network failure) that we should apologize for?"
 *
 * Letting raw backend messages leak is the easy wrong answer —
 * users don't need to see "ECONNREFUSED" or "nodemailer transport
 * closed". They need a clear next step.
 *
 * Classification
 * ==============
 *
 *   - `rate_limit` (429) — user did something legitimate, just
 *     too fast. Render as an info / warning, NOT an error. The
 *     backend message is usually descriptive ("you've sent 20
 *     invites in the last hour") so we pass it through verbatim.
 *
 *   - `server` (5xx, fetch throw) — we messed up. Render the
 *     "on our side, will be fixed" copy. The actual server
 *     message is almost never user-actionable.
 *
 *   - `network` (fetch throw with no response) — transient
 *     connectivity issue. Distinct from `server` because the
 *     recovery hint is different ("check your connection" vs
 *     "try again later").
 *
 *   - `client` (other 4xx) — user-actionable. Pass the server's
 *     message through verbatim — it's usually something like
 *     "Password must be at least 8 characters" which we don't
 *     want to paraphrase.
 *
 * Usage:
 *
 *     try {
 *       await someBackendCall()
 *     } catch (err) {
 *       const classified = classifyError(err)
 *       setError(classified)  // state type matches ClassifiedError
 *     }
 *
 *     // In render:
 *     {error ? <ErrorBanner error={error} /> : null}
 *
 * or for one-shot "I just want a string":
 *
 *     setErrorMessage(classifyError(err).message)
 */

import { PlatformApiError } from './api'

export type ClassifiedErrorKind = 'rate_limit' | 'server' | 'network' | 'client'

export interface ClassifiedError {
  kind: ClassifiedErrorKind
  /**
   * UI tone for rendering — `info` is a warning-toned card (we
   * tell the user something happened but it's not a failure on
   * our end), `error` is a destructive-toned card. The
   * distinction matters because users interpret colors before
   * they read copy.
   */
  level: 'info' | 'error'
  /** Short headline. Rendered bold / larger than the message. */
  title: string
  /** One-sentence explanation. Safe to show to end users. */
  message: string
}

/**
 * Classify an unknown caught value into a user-facing error shape.
 * Always returns — never throws, never returns null. Accepts
 * anything (the `catch (err)` binding is `unknown` in strict mode).
 */
export function classifyError(err: unknown): ClassifiedError {
  // Our typed API error carries status + body, which is enough to
  // distinguish rate limit / server / client.
  if (err instanceof PlatformApiError) {
    const status = err.status

    // 429 Too Many Requests — rate limit. Backend message is
    // usually descriptive enough to render verbatim.
    if (status === 429) {
      return {
        kind: 'rate_limit',
        level: 'info',
        title: 'Slow down',
        message: err.message || 'Too many requests. Try again in a few minutes.',
      }
    }

    // 500-599 Server errors — ours to fix. Never surface the raw
    // message because it often leaks implementation detail
    // ("pool exhausted", "SMTP 421 timeout") that a user can't
    // act on.
    if (status >= 500 && status < 600) {
      return {
        kind: 'server',
        level: 'error',
        title: 'Something went wrong on our side',
        message: "We've been notified and are looking into it. Please try again in a few minutes.",
      }
    }

    // Other 4xx — pass the server message through. This covers
    // validation errors, 401 bad credentials, 404 not found,
    // 409 conflicts, etc., all of which need to say exactly
    // what went wrong.
    return {
      kind: 'client',
      level: 'error',
      title: 'Request rejected',
      message: err.message || 'Something was wrong with the request.',
    }
  }

  // fetch throws TypeError on network failures (DNS, unreachable,
  // blocked by browser extensions, CORS preflight denied without
  // response body, etc.). Distinct from "backend returned 500"
  // because the recovery hint differs.
  if (err instanceof TypeError) {
    return {
      kind: 'network',
      level: 'error',
      title: "Can't reach our servers",
      message:
        'Check your internet connection and try again. If the problem persists, our servers may be down.',
    }
  }

  // Anything else — unknown error type. Treat as server-side.
  // `err` might be a plain object, a string, or null. We don't
  // know, so we don't trust its message.
  return {
    kind: 'server',
    level: 'error',
    title: 'Something went wrong',
    message: 'Something unexpected happened. Please try again in a few minutes.',
  }
}

/**
 * Convenience: classify and return just the message. Useful for
 * pages that still store errors as plain strings and only want to
 * upgrade the friendliness without restructuring state.
 */
export function friendlyErrorMessage(err: unknown): string {
  return classifyError(err).message
}

/**
 * Renders a `ClassifiedError` as a styled banner. Picks the tone
 * from `error.level`:
 *
 *   - `info` → warning-toned card (yellow/amber). Used for
 *     rate_limit where the user hasn't done anything wrong, they
 *     just need to slow down.
 *
 *   - `error` → destructive-toned card (red). Used for
 *     server/network/client errors where something is actually
 *     broken and the user needs to take a different action.
 *
 * Both variants show a bold title line and a plain explanation
 * line below. Kept in this module so every page using
 * `classifyError` has a drop-in renderer — callers don't need to
 * thread `level` through their own JSX.
 *
 * Usage:
 *
 *     const [err, setErr] = useState<ClassifiedError | null>(null)
 *     // ...
 *     try { await something() }
 *     catch (e) { setErr(classifyError(e)) }
 *     // ...
 *     {err ? <ErrorBanner error={err} /> : null}
 */
export function ErrorBanner({ error, className }: { error: ClassifiedError; className?: string }) {
  const toneClass =
    error.level === 'info'
      ? 'border-warning/40 bg-warning/5 text-warning'
      : 'border-destructive/40 bg-destructive/5 text-destructive'
  return (
    <div
      className={`mt-3 rounded-md border px-3 py-2 text-xs ${toneClass} ${className ?? ''}`}
      role={error.level === 'error' ? 'alert' : 'status'}
    >
      <div className="font-semibold">{error.title}</div>
      <div className="mt-0.5 opacity-90">{error.message}</div>
    </div>
  )
}
